import type {OttopilotEventTemplate} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/ottopilotEventsWorkspaceCapability/domain/OttopilotEventTemplate";
import type {EventContainerTemplate} from "~/domainModels/workspaceModules/eventContainersModule";
import {normalizeAmaronCallsPluralDisplayLabel} from "~/domainModels/workspaceSpecific/amaron";
import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {z} from "zod";
import {PageType} from "@vesta/vos-types/modules/workspaceLevel/workspaceUiConfigs/domain/WorkspaceConfig";
import {uuidZodValidator} from "@vesta/global-common-typescript/validators/zodValidators";
import {resolveConfigEventListHref, resolveConfigPageHrefByType} from "~/workspaceConfig/configHref";

export const eventContainerProfileSearchSchema = z.object({
    from: z.string().optional(),
    eventContainerTemplateId: uuidZodValidator.optional(),
});

export type EventContainerProfileSearch = z.infer<typeof eventContainerProfileSearchSchema>;

export const EVENT_CONTAINER_PROFILE_FROM = {
    leads: "event-containers",
    inQueue: "event-containers-inqueue",
    archived: "event-containers-archived",
    myDayPlan: "my-day-plan",
    dispositionTracker: "disposition-tracker",
} as const;

const EVENTS_FROM_PREFIX = "events:";

export function buildEventContainerProfileFromForList(args: {
    isArchivedView: boolean;
    isInQueueView: boolean;
}): string {
    if (args.isArchivedView === true) {
        return EVENT_CONTAINER_PROFILE_FROM.archived;
    }
    if (args.isInQueueView === true) {
        return EVENT_CONTAINER_PROFILE_FROM.inQueue;
    }
    return EVENT_CONTAINER_PROFILE_FROM.leads;
}

export function buildEventContainerProfileFromForEventTemplate(eventTemplateId: Uuid): string {
    return `${EVENTS_FROM_PREFIX}${eventTemplateId}`;
}

export function buildEventContainerProfileNavigateSearch(from: string): EventContainerProfileSearch {
    return {from};
}

export function resolveEventContainerProfileParentBreadcrumb(args: {
    from: string | undefined;
    workspaceId: Uuid;
    eventContainerTemplate: EventContainerTemplate;
    eventTemplates: ReadonlyArray<OttopilotEventTemplate>;
    isAmaronWorkspace: boolean;
}): {displayName: string; to: string} {
    const eventContainersPluralLabel = args.eventContainerTemplate.configuration.moduleDisplayNameVariants.pluralSentenceCase;
    const from = args.from ?? EVENT_CONTAINER_PROFILE_FROM.leads;

    if (from === EVENT_CONTAINER_PROFILE_FROM.leads) {
        return {
            displayName: eventContainersPluralLabel,
            to: resolveConfigPageHrefByType({
                workspaceId: args.workspaceId,
                pageType: PageType.EventContainerWithEvents,
            }) ?? `/w/${args.workspaceId}`,
        };
    }

    if (from === EVENT_CONTAINER_PROFILE_FROM.inQueue) {
        return {
            displayName: "In Queue",
            to: resolveConfigPageHrefByType({
                workspaceId: args.workspaceId,
                pageType: PageType.AgentView,
            }) ?? `/w/${args.workspaceId}`,
        };
    }

    if (from === EVENT_CONTAINER_PROFILE_FROM.archived) {
        return {
            displayName: `Archived ${eventContainersPluralLabel}`,
            to: resolveConfigPageHrefByType({
                workspaceId: args.workspaceId,
                pageType: PageType.EventContainerWithEvents,
            }) ?? `/w/${args.workspaceId}`,
        };
    }

    if (from === EVENT_CONTAINER_PROFILE_FROM.myDayPlan) {
        return {
            displayName: "My Day Plan",
            to: resolveConfigPageHrefByType({
                workspaceId: args.workspaceId,
                pageType: PageType.AgentView,
            }) ?? `/w/${args.workspaceId}`,
        };
    }

    if (from === EVENT_CONTAINER_PROFILE_FROM.dispositionTracker) {
        return {
            displayName: "Disposition Tracker",
            to: resolveConfigPageHrefByType({
                workspaceId: args.workspaceId,
                pageType: PageType.DispositionTracker,
            }) ?? `/w/${args.workspaceId}`,
        };
    }

    if (from.startsWith(EVENTS_FROM_PREFIX) === true) {
        const eventTemplateId = from.slice(EVENTS_FROM_PREFIX.length) as Uuid;
        const eventTemplate = args.eventTemplates.find((template) => String(template.id) === eventTemplateId);
        const pluralLabel =
            eventTemplate === undefined
                ? "Events"
                : args.isAmaronWorkspace === true
                  ? normalizeAmaronCallsPluralDisplayLabel(eventTemplate.configuration.displayNameVariants.pluralSentenceCase)
                  : eventTemplate.configuration.displayNameVariants.pluralSentenceCase;

        return {
            displayName: pluralLabel,
            to: resolveConfigEventListHref({
                workspaceId: args.workspaceId,
                eventTemplateId,
            }) ?? `/w/${args.workspaceId}`,
        };
    }

    return {
        displayName: eventContainersPluralLabel,
        to: resolveConfigPageHrefByType({
            workspaceId: args.workspaceId,
            pageType: PageType.EventContainerWithEvents,
        }) ?? `/w/${args.workspaceId}`,
    };
}

export function resolveEventContainerProfileBreadcrumbs(args: {
    from: string | undefined;
    workspaceId: Uuid;
    eventContainerTemplate: EventContainerTemplate;
    eventTemplates: ReadonlyArray<OttopilotEventTemplate>;
    isAmaronWorkspace: boolean;
}): Array<{displayName: string; to: string | null}> {
    const parent = resolveEventContainerProfileParentBreadcrumb(args);

    return [
        parent,
        {
            displayName: `${args.eventContainerTemplate.configuration.moduleDisplayNameVariants.singularSentenceCase} profile`,
            to: null,
        },
    ];
}
